// Exact section boundaries keep editorial instructions separate from participant content.
export function section(source: string, start: string, end?: string): string {
  const from = source.indexOf(start);
  if (from < 0) throw new Error(`Falta la sección: ${start}`);
  const to = end ? source.indexOf(end, from + start.length) : source.length;
  if (to < 0) throw new Error(`Falta el cierre de sección: ${end}`);
  return clean(source.slice(from + start.length, to));
}

function clean(value: string) { return value.trim().replace(/(?:\n\s*---\s*)+$/, "").trim(); }
function body(source: string, start: string, end: string, marker: string) {
  return section(section(source, start, end), marker);
}

export type ChoiceCase = { title: string; prompt: string; question: string; options: string[]; answer: number; explanation: string };
export type RevealCase = { title: string; prompt: string; explanation: string };
export type LaterModuleContent = ReturnType<typeof parseLaterModule>;

export function parseLaterModule(source: string, number: 3 | 4) {
  const isThree = number === 3;
  const closeStart = "# 9. PIEZA 4 · CERRAR";
  const practiceStart = isThree ? "# 10. PROPUESTA PRÁCTICA PARA EL HOGAR" : "# 12. PROPUESTA PRÁCTICA PARA EL HOGAR";
  const reflectionStart = isThree ? "# 11. PREGUNTA DE REFLEXIÓN" : "# 13. PREGUNTA DE REFLEXIÓN";
  const resourceStart = isThree ? "# 12. RECURSO DESCARGABLE" : "# 14. RECURSO DESCARGABLE";
  const sourcesStart = isThree ? "# 13. FUENTES PRINCIPALES DEL MÓDULO" : "# 15. FUENTES PRINCIPALES DEL MÓDULO";
  const supportStart = isThree ? "# 7. TEXTO DE APOYO" : "# 7. Texto de apoyo";
  const interactionStart = isThree ? "# 8. INTERACCIÓN · ¿Límite, castigo o acuerdo?" : "# 8. INTERACCIÓN · ¿Cómo abrirías la conversación?";
  const objectivesCopy = section(source, "# 2. Objetivos del módulo", "# 3. VÍDEO DE BIENVENIDA");
  const objectives = [...objectivesCopy.matchAll(/^\d+\. (.+)$/gm)].map(match => match[1]);
  if (objectives.length !== 12) throw new Error(`M${number}: se esperaban doce objetivos.`);
  return {
    number,
    title: isThree ? "Límites que cuidan, acuerdos digitales y autonomía" : "Presión social, autoestima, redes y riesgos",
    summary: section(source, "# 1. Resumen breve del módulo", "# 2. Objetivos del módulo"),
    objectives,
    welcomeTitle: isThree ? "Poner límites sin vivir negociando cada tarde" : "Hay cosas que no vamos a poder ver",
    welcome: body(source, "# 3. VÍDEO DE BIENVENIDA", "# 4. PIEZA 1 · COMPRENDER", "## Guion"),
    understandTitle: isThree ? "Autonomía no significa ausencia de límites" : "Pertenecer importa. Y mucho.",
    understand: body(source, "# 4. PIEZA 1 · COMPRENDER", "# 5. PIEZA 2 · MIRAR", "## Guion del vídeo"),
    lookTitle: isThree ? "La pregunta no es solo “¿cuántas horas?”" : "Cuando mirar a los demás cambia cómo me miro a mí",
    look: body(source, "# 5. PIEZA 2 · MIRAR", "# 6. PIEZA 3 · PROFUNDIZAR", "## Texto principal"),
    deepenTitle: isThree ? "Un buen acuerdo no empieza por “entrégame el móvil”" : "Hay conversaciones incómodas que protegen",
    deepen: body(source, "# 6. PIEZA 3 · PROFUNDIZAR", supportStart, "## Guion del vídeo"),
    support: section(source, supportStart, interactionStart),
    interaction: section(source, interactionStart, closeStart),
    closeTitle: isThree ? "Menos normas. Más claras." : "Observar, conversar o pedir ayuda",
    close: body(source, closeStart, isThree ? practiceStart : "# 10. ACTIVIDAD GUIADA", "## Texto principal"),
    closingCases: isThree ? "" : section(source, "# 10. ACTIVIDAD GUIADA · ¿Observo, converso o pido ayuda?", "# 11. Cierre del contenido"),
    closingMessage: isThree ? "" : section(source, "# 11. Cierre del contenido", practiceStart),
    practiceTitle: isThree ? "Un acuerdo, siete días" : "Una conversación que no quiero seguir aplazando",
    practice: section(source, practiceStart, reflectionStart),
    reflection: section(source, reflectionStart, resourceStart),
    resourceTitle: isThree ? "Nuestro acuerdo digital familiar" : "Cuando algo me preocupa",
    resource: section(source, resourceStart, sourcesStart),
    sources: section(source, sourcesStart, isThree ? "# 14. DISTRIBUCIÓN DE VÍDEOS" : "# 16. DISTRIBUCIÓN DE VÍDEOS"),
    itinerary: isThree ? "" : section(source, "# 19. CONEXIÓN CON TODO EL ITINERARIO", "# 20. IDEA QUE DEBERÍA QUEDAR AL TERMINAR"),
    finalIdea: section(source, isThree ? "# 17. IDEA QUE DEBERÍA QUEDAR AL TERMINAR" : "# 20. IDEA QUE DEBERÍA QUEDAR AL TERMINAR")
  };
}

export function splitCases(value: string, kind: "Situación" | "Caso") {
  const pattern = new RegExp(`^## ${kind} (\\d+)[^\\n]*\\n`, "gm");
  const matches = [...value.matchAll(pattern)];
  return matches.map((match, index) => ({
    title: `${kind} ${match[1]}`,
    content: clean(value.slice(match.index! + match[0].length, matches[index + 1]?.index ?? value.length))
  }));
}

export function parseReveals(value: string): RevealCase[] {
  return splitCases(value, "Situación").map(({title, content}) => {
    const answerStart = content.indexOf("**");
    if (answerStart < 0) throw new Error(`Falta orientación en ${title}`);
    return {title, prompt: clean(content.slice(0, answerStart)), explanation: clean(content.slice(answerStart))};
  });
}

export function parseOpeningCases(value: string): ChoiceCase[] {
  return splitCases(value, "Situación").map(({title, content}) => {
    const explanation = section(content, "### Respuesta recomendada");
    const letter = explanation.match(/^\*\*([ABC])\./)?.[1];
    if (!letter) throw new Error(`Respuesta no válida: ${title}`);
    return {
      title, prompt: clean(content.slice(0, content.indexOf("### A"))), question: "¿Cómo abrirías la conversación?",
      options: [section(content, "### A", "### B"), section(content, "### B", "### C"), section(content, "### C", "### Respuesta recomendada")],
      answer: letter.charCodeAt(0) - 65, explanation
    };
  });
}

export function parseLookCases(value: string): ChoiceCase[] {
  const starts = ["### Laura, 14 años", "### Mario, 16 años"];
  const ends = ["## Segundo escenario", "## Mensaje de cierre"];
  const questions = ["¿Cuál sería tu primera reacción?", "¿Qué debería preocuparnos más?"];
  return starts.map((start, i) => {
    const content = section(value, start, ends[i]);
    const options = [...content.matchAll(/^[A-D]\. (.+)$/gm)].map(match => match[1]);
    const explanation = section(content, "### Respuesta recomendada");
    const letter = explanation.match(/^\*\*([A-D])\./)?.[1];
    if (options.length !== 4 || !letter) throw new Error("Caso Mirar incompleto");
    return { title: start.replace("### ", ""), prompt: section(content, "", "### " + questions[i]), question: questions[i], options, answer: letter.charCodeAt(0) - 65, explanation };
  });
}

export const responseLevels = ["Observar", "Conversar y actuar", "Pedir ayuda"];
export function parseClosingCases(value: string): ChoiceCase[] {
  return splitCases(value, "Caso").map(({title, content}) => {
    const explanation = section(content, "### Respuesta");
    const answer = responseLevels.findIndex(level => explanation.startsWith(`**${level}.**`));
    if (answer < 0) throw new Error(`Nivel no válido: ${title}`);
    return {title, prompt: section(content, "", "### Respuesta"), question: "¿Observo, converso o pido ayuda?", options: responseLevels, answer, explanation};
  });
}

export const protectionAreas = ["Descanso", "Estudio y atención", "Convivencia", "Seguridad y privacidad", "Ocio digital", "Compras y dinero", "Otro"];
export function toggleArea(selected: string[], area: string) {
  if (!protectionAreas.includes(area)) return selected;
  return selected.includes(area) ? selected.filter(item => item !== area) : selected.length < 2 ? [...selected, area] : selected;
}
